import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiImage, FiPlus, FiEdit2, FiTrash2, FiX, FiCheck,
  FiUpload, FiEye, FiEyeOff,
} from 'react-icons/fi';
import API from '../utils/api';
import toast from 'react-hot-toast';

/* ─── Styles ─────────────────────────────────────────────────────────── */
const card = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  padding: '20px 24px',
};
const label = { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 };
const input = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px',
  border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none',
};
const btnPrimary = {
  background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8,
  padding: '8px 18px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
  display: 'flex', alignItems: 'center', gap: 6,
};
const btnGhost = {
  background: 'transparent', border: '1px solid #e5e7eb', borderRadius: 8,
  padding: '7px 14px', fontWeight: 500, fontSize: 13, cursor: 'pointer',
  display: 'flex', alignItems: 'center', gap: 5, color: '#374151',
};

/* ─── Empty form ─────────────────────────────────────────────────────── */
const EMPTY = { name: '', logoUrl: '', bunnyFilename: '', isActive: true };

/* ─── Upload helper — reuses existing /api/upload route ─────────────── */
const uploadToAPI = async (file) => {
  const form = new FormData();
  form.append('image', file);
  const { data } = await API.post('/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  if (!data.success) throw new Error(data.message || 'Upload failed');
  return { url: data.url, filename: data.filename };
};

/* ─── Drag-and-drop list item ────────────────────────────────────────── */
function DraggableLogoCard({ logo, index, onEdit, onDelete, onToggle, onDragStart, onDragOver, onDrop }) {
  const isDragging = useRef(false);

  return (
    <div
      draggable
      onDragStart={(e) => { isDragging.current = true; onDragStart(index); e.dataTransfer.effectAllowed = 'move'; }}
      onDragEnd={() => { isDragging.current = false; }}
      onDragOver={(e) => { e.preventDefault(); onDragOver(index); }}
      onDrop={(e) => { e.preventDefault(); onDrop(index); }}
      style={{
        display: 'flex', alignItems: 'center', gap: 16,
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
        padding: '12px 16px', cursor: 'grab', userSelect: 'none',
        transition: 'box-shadow 0.15s',
      }}
    >
      {/* Drag handle dots */}
      <div style={{ color: '#9ca3af', fontSize: 18, cursor: 'grab', flexShrink: 0 }}>⠿</div>

      {/* Logo preview */}
      <div style={{
        width: 80, height: 48, borderRadius: 6, border: '1px solid #f3f4f6',
        background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', flexShrink: 0,
      }}>
        <img
          src={logo.logoUrl}
          alt={logo.name}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      </div>

      {/* Name */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {logo.name}
        </div>
        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
          Order #{logo.order}
        </div>
      </div>

      {/* Active badge */}
      <span style={{
        fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20,
        background: logo.isActive ? '#d1fae5' : '#f3f4f6',
        color: logo.isActive ? '#065f46' : '#6b7280',
        flexShrink: 0,
      }}>
        {logo.isActive ? 'Active' : 'Hidden'}
      </span>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <button onClick={() => onToggle(logo)} title={logo.isActive ? 'Hide' : 'Show'} style={{ ...btnGhost, padding: '6px 10px' }}>
          {logo.isActive ? <FiEyeOff size={14} /> : <FiEye size={14} />}
        </button>
        <button onClick={() => onEdit(logo)} title="Edit" style={{ ...btnGhost, padding: '6px 10px' }}>
          <FiEdit2 size={14} />
        </button>
        <button onClick={() => onDelete(logo)} title="Delete" style={{ ...btnGhost, padding: '6px 10px', color: '#ef4444', borderColor: '#fecaca' }}>
          <FiTrash2 size={14} />
        </button>
      </div>
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────────────── */
export default function AdminClientLogos() {
  const [logos, setLogos]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm]           = useState(EMPTY);
  const [editId, setEditId]       = useState(null);
  const [saving, setSaving]       = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview]     = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Drag state
  const dragIndex = useRef(null);

  /* ── Fetch ── */
  const fetchLogos = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/client-logos');
      setLogos(data.logos || []);
    } catch {
      toast.error('Failed to load client logos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLogos(); }, [fetchLogos]);

  /* ── Modal helpers ── */
  const openAdd = () => {
    setForm(EMPTY); setEditId(null); setPreview(''); setShowModal(true);
  };
  const openEdit = (logo) => {
    setForm({ name: logo.name, logoUrl: logo.logoUrl, bunnyFilename: logo.bunnyFilename || '', isActive: logo.isActive });
    setEditId(logo.id);
    setPreview(logo.logoUrl);
    setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setEditId(null); setForm(EMPTY); setPreview(''); };

  /* ── File upload (converts to webp via /api/upload) ── */
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const { url, filename } = await uploadToAPI(file);
      setForm(f => ({ ...f, logoUrl: url, bunnyFilename: filename }));
      setPreview(url);
      toast.success('Image uploaded — save to confirm');
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  /* ── Save (create / update) ── */
  const handleSave = async () => {
    if (!form.name.trim())   return toast.error('Name is required');
    if (!form.logoUrl.trim()) return toast.error('Please upload a logo image');
    try {
      setSaving(true);
      if (editId) {
        await API.put(`/client-logos/${editId}`, form);
        toast.success('Logo updated');
      } else {
        await API.post('/client-logos', form);
        toast.success('Logo added');
      }
      closeModal();
      fetchLogos();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  /* ── Toggle active ── */
  const handleToggle = async (logo) => {
    try {
      await API.put(`/client-logos/${logo.id}`, { isActive: !logo.isActive });
      toast.success(logo.isActive ? 'Hidden from homepage' : 'Now visible on homepage');
      fetchLogos();
    } catch {
      toast.error('Failed to update');
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await API.delete(`/client-logos/${deleteTarget.id}`);
      toast.success('Logo deleted');
      setDeleteTarget(null);
      fetchLogos();
    } catch {
      toast.error('Failed to delete');
    }
  };

  /* ── Drag & drop reorder ── */
  const handleDragStart = (index) => { dragIndex.current = index; };
  const handleDragOver  = (_index) => { /* browser needs this */ };
  const handleDrop = async (dropIndex) => {
    const from = dragIndex.current;
    if (from === null || from === dropIndex) return;

    const reordered = [...logos];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(dropIndex, 0, moved);

    // Optimistically update UI
    const withNewOrder = reordered.map((l, i) => ({ ...l, order: i + 1 }));
    setLogos(withNewOrder);
    dragIndex.current = null;

    try {
      await API.patch('/client-logos/reorder', {
        order: withNewOrder.map(l => ({ id: l.id, order: l.order })),
      });
      toast.success('Order saved');
    } catch {
      toast.error('Failed to save order');
      fetchLogos(); // revert to server state
    }
  };

  /* ── Render ── */
  return (
    <div style={{ padding: '32px 24px' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h2 style={{ fontWeight: 700, fontSize: '1.4rem', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiImage size={20} color="#6366f1" /> Our Clients
          </h2>
          <p style={{ color: '#9ca3af', fontSize: 13, marginTop: 4 }}>
            Logos shown in the homepage carousel — drag rows to reorder
          </p>
        </div>
        <button style={btnPrimary} onClick={openAdd}>
          <FiPlus size={16} /> Add Logo
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}>Loading…</div>
      ) : logos.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', padding: 48, color: '#9ca3af' }}>
          <FiImage size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
          <div>No client logos yet — click <strong>Add Logo</strong> to get started</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {logos.map((logo, index) => (
            <DraggableLogoCard
              key={logo.id}
              logo={logo}
              index={index}
              onEdit={openEdit}
              onDelete={setDeleteTarget}
              onToggle={handleToggle}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            />
          ))}
        </div>
      )}

      {/* ── Add / Edit Modal ── */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontWeight: 700, fontSize: '1.1rem' }}>
                {editId ? 'Edit Logo' : 'Add Client Logo'}
              </h3>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                <FiX size={20} />
              </button>
            </div>

            {/* Name */}
            <div style={{ marginBottom: 16 }}>
              <label style={label}>Client / Brand Name *</label>
              <input
                style={input}
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Acme Corp"
              />
            </div>

            {/* Logo upload */}
            <div style={{ marginBottom: 16 }}>
              <label style={label}>Logo Image *</label>

              {/* Preview */}
              {preview && (
                <div style={{
                  width: '100%', height: 100, borderRadius: 8, border: '1px solid #e5e7eb',
                  background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 10, overflow: 'hidden',
                }}>
                  <img src={preview} alt="preview" style={{ maxHeight: 80, maxWidth: '90%', objectFit: 'contain' }} />
                </div>
              )}

              <label style={{
                display: 'flex', alignItems: 'center', gap: 8, cursor: uploading ? 'not-allowed' : 'pointer',
                border: '1px dashed #d1d5db', borderRadius: 8, padding: '10px 14px',
                color: '#6b7280', fontSize: 13, background: '#f9fafb',
              }}>
                <FiUpload size={15} />
                {uploading ? 'Uploading & converting to WebP…' : preview ? 'Replace image' : 'Choose image (PNG, JPG, WebP, SVG)'}
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp,image/svg+xml"
                  style={{ display: 'none' }}
                  disabled={uploading}
                  onChange={handleFileChange}
                />
              </label>
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                Image will be automatically converted to WebP and uploaded to CDN
              </div>
            </div>

            {/* Active toggle */}
            <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="checkbox"
                id="isActive"
                checked={form.isActive}
                onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              <label htmlFor="isActive" style={{ fontSize: 14, color: '#374151', cursor: 'pointer' }}>
                Show on homepage
              </label>
            </div>

            {/* Footer buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button style={btnGhost} onClick={closeModal} disabled={saving}>
                Cancel
              </button>
              <button style={btnPrimary} onClick={handleSave} disabled={saving || uploading}>
                <FiCheck size={15} />
                {saving ? 'Saving…' : editId ? 'Update' : 'Add Logo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleteTarget && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 10px', fontWeight: 700 }}>Delete Logo?</h3>
            <p style={{ color: '#6b7280', fontSize: 14 }}>
              <strong>{deleteTarget.name}</strong> will be permanently removed from the carousel and deleted from CDN.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button style={btnGhost} onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button
                style={{ ...btnPrimary, background: '#ef4444' }}
                onClick={handleDelete}
              >
                <FiTrash2 size={14} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
